import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import { useForm } from 'react-hook-form'
import {
    PlusIcon,
    PencilIcon,
    TrashIcon,
    QuestionMarkCircleIcon,
    XMarkIcon
} from '@heroicons/react/24/outline'
import { faqAPI } from '../../services/api'
import LoadingSpinner from '../../components/Common/LoadingSpinner'
import ErrorMessage from '../../components/Common/ErrorMessage'
import PermissionWrapper from '../../components/Admin/PermissionWrapper'
import toast from 'react-hot-toast'

const AdminFAQs = () => {
    return (
        <PermissionWrapper requiredPermission="faqs">
            <AdminFAQsContent />
        </PermissionWrapper>
    )
}

const AdminFAQsContent = () => {
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [editingFAQ, setEditingFAQ] = useState(null)
    const queryClient = useQueryClient()

    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['admin-faqs'],
        queryFn: faqAPI.getFAQs,
    })

    const { register, handleSubmit, formState: { errors }, reset, watch } = useForm()

    const createMutation = useMutation({
        mutationFn: faqAPI.createFAQ,
        onSuccess: () => {
            toast.success('FAQ created successfully!')
            queryClient.invalidateQueries(['admin-faqs'])
            setIsModalOpen(false)
            reset()
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to create FAQ')
        },
    })

    const updateMutation = useMutation({
        mutationFn: ({ id, data }) => faqAPI.updateFAQ(id, data),
        onSuccess: () => {
            toast.success('FAQ updated successfully!')
            queryClient.invalidateQueries(['admin-faqs'])
            setIsModalOpen(false)
            setEditingFAQ(null)
            reset()
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to update FAQ')
        },
    })

    const deleteMutation = useMutation({
        mutationFn: faqAPI.deleteFAQ,
        onSuccess: () => {
            toast.success('FAQ deleted successfully!')
            queryClient.invalidateQueries(['admin-faqs'])
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to delete FAQ')
        },
    })

    const handleEdit = (faq) => {
        setEditingFAQ(faq)
        reset(faq)
        setIsModalOpen(true)
    }

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this FAQ?')) {
            deleteMutation.mutate(id)
        }
    }

    const onSubmit = (data) => {
        if (editingFAQ) {
            updateMutation.mutate({ id: editingFAQ._id, data })
        } else {
            createMutation.mutate(data)
        }
    }

    const handleCloseModal = () => {
        setIsModalOpen(false)
        setEditingFAQ(null)
        reset()
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <ErrorMessage message="Failed to load FAQs" onRetry={refetch} />
            </div>
        )
    }

    const faqs = data?.data?.faqs || []

    const getCategoryColor = (category) => {
        const colors = {
            'general': 'bg-accent-100 text-accent-800',
            'admission': 'bg-secondary-100 text-secondary-800',
            'fees': 'bg-primary-100 text-primary-800',
            'courses': 'bg-gold-100 text-gold-800',
            'university': 'bg-accent-100 text-accent-800',
            'eligibility': 'bg-secondary-100 text-secondary-800',
            'career': 'bg-primary-100 text-primary-800',
            'placement': 'bg-gold-100 text-gold-800',
        }
        return colors[category] || colors['general']
    }

    return (
        <>
            <Helmet>
                <title>Manage FAQs | Margdarshak Admin</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            <div className="min-h-screen bg-gray-50">
                {/* Quick Actions */}
                <div className="bg-white shadow-sm border-b border-gray-200">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
                        <div className="flex justify-end">
                            <button
                                onClick={() => setIsModalOpen(true)}
                                className="btn-primary"
                            >
                                <PlusIcon className="w-5 h-5 mr-2" />
                                Add FAQ
                            </button>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    {isLoading ? (
                        <div className="flex justify-center py-12">
                            <LoadingSpinner size="lg" />
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {faqs.map((faq) => (
                                <motion.div
                                    key={faq._id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-3 mb-3">
                                                <div className="w-10 h-10 bg-accent-100 rounded-lg flex items-center justify-center">
                                                    <QuestionMarkCircleIcon className="w-5 h-5 text-accent-600" />
                                                </div>
                                                <div>
                                                    <h3 className="text-lg font-semibold text-gray-900">
                                                        {faq.question}
                                                    </h3>
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(faq.category)}`}>
                                                        {faq.category}
                                                    </span>
                                                </div>
                                            </div>
                                            <p className="text-gray-600 leading-relaxed">
                                                {faq.answer}
                                            </p>
                                        </div>
                                        <div className="flex space-x-2 ml-4">
                                            <button
                                                onClick={() => handleEdit(faq)}
                                                className="text-accent-600 hover:text-accent-900 p-1"
                                            >
                                                <PencilIcon className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(faq._id)}
                                                className="text-red-600 hover:text-red-900 p-1"
                                            >
                                                <TrashIcon className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Add/Edit Modal */}
                <AnimatePresence>
                    {isModalOpen && (
                        <div className="fixed inset-0 z-[9999] overflow-y-auto">
                            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                                <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={handleCloseModal}></div>

                                <motion.div
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.95 }}
                                    className="inline-block w-full max-w-2xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl relative z-[10000]"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div className="flex items-center justify-between mb-6">
                                        <h3 className="text-2xl font-bold text-gray-900">
                                            {editingFAQ ? 'Edit FAQ' : 'Add New FAQ'}
                                        </h3>
                                        <button
                                            onClick={handleCloseModal}
                                            className="p-2 text-gray-400 hover:text-gray-600 relative z-[10001]"
                                        >
                                            <XMarkIcon className="w-6 h-6" />
                                        </button>
                                    </div>

                                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Question *
                                            </label>
                                            <input
                                                type="text"
                                                {...register('question', { required: 'Question is required' })}
                                                className="input"
                                                placeholder="Enter the question"
                                            />
                                            {errors.question && (
                                                <p className="mt-1 text-sm text-red-600">{errors.question.message}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Answer *
                                            </label>
                                            <textarea
                                                {...register('answer', { required: 'Answer is required' })}
                                                rows={4}
                                                className="input"
                                                placeholder="Enter the answer"
                                            />
                                            {errors.answer && (
                                                <p className="mt-1 text-sm text-red-600">{errors.answer.message}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Category *
                                            </label>
                                            <select
                                                {...register('category', { required: 'Category is required' })}
                                                className="input"
                                            >
                                                <option value="">Select Category</option>
                                                <option value="general">General</option>
                                                <option value="admission">Admission</option>
                                                <option value="fees">Fees</option>
                                                <option value="courses">Courses</option>
                                                <option value="university">University</option>
                                                <option value="eligibility">Eligibility</option>
                                                <option value="career">Career</option>
                                                <option value="placement">Placement</option>
                                            </select>
                                            {errors.category && (
                                                <p className="mt-1 text-sm text-red-600">{errors.category.message}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Order
                                            </label>
                                            <input
                                                type="number"
                                                {...register('order', { min: 1 })}
                                                className="input"
                                                placeholder="Display order"
                                            />
                                        </div>

                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                id="active"
                                                {...register('active')}
                                                className="mr-2 h-4 w-4 text-accent-600 focus:ring-accent-500 border-gray-300 rounded"
                                            />
                                            <label htmlFor="active" className="text-sm text-gray-700">
                                                Active FAQ
                                            </label>
                                        </div>

                                        <div className="flex justify-end space-x-4">
                                            <button
                                                type="button"
                                                onClick={handleCloseModal}
                                                className="btn-secondary"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={createMutation.isPending || updateMutation.isPending}
                                                className="btn-primary"
                                            >
                                                {createMutation.isPending || updateMutation.isPending ? (
                                                    <div className="flex items-center space-x-2">
                                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                        <span>Saving...</span>
                                                    </div>
                                                ) : (
                                                    editingFAQ ? 'Update FAQ' : 'Create FAQ'
                                                )}
                                            </button>
                                        </div>
                                    </form>
                                </motion.div>
                            </div>
                        </div>
                    )}
                </AnimatePresence>
            </div>
        </>
    )
}

export default AdminFAQs
